import { useState, type ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { usePulseRuns } from '../../../lib/use-pulse-runs';
import { deleteRoutine, patchRoutine, useRoutines, type WebRoutine } from '../../../lib/routines';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { routineIsOn, routineStatusBadge, routineToggleEnabled } from '../../../lib/routine-status';
import { ROUTINE_DETAILS_PAGE } from '../../../lib/routines-bi';
import { showsTaskCount } from '../../../lib/run-group';
import { useNarrow } from '../../../lib/use-narrow';
import { Modal } from '../../Modal';
import { AgentDot } from '../../AgentDot';
import { SkillConnectorLine } from '../agents/SkillConnectorLine';
import { EmptyState } from '../agents/EmptyState';
import { Badge, Button, Card, Switch, TextButton } from '../ui';
import { useRoutineCopy } from './use-routine-copy';
import { RoutineEditForm, useRoutineEdit } from './RoutineEditForm';
import styles from './routines.module.css';

// The routine drill-in — a DS centered <Modal>, capped at 650 with the body scrolling inside it (the
// house rule for every dialog; see CLAUDE.md → "Modals"). It answers, in order: what state is this in
// and why, the facts (agent / Space / schedule / next run / activations), what it actually does, and
// what it has been doing lately.
//
// Every one of those is the SAME divider-parted label/value row (Figma 18216-326313): a bold label in
// the left column, the value in the right, a hairline between each pair. Status leads, carrying the
// pause toggle in the value column and the status pill pushed to the far edge — the reader's first
// question is whether this thing is running, and the row that answers it is also the one that changes it.
//
// **A phone renders the PHONE's design, not this one narrowed.** Below 720 the body is the native detail
// screen — the facts in cards under their own headings, the run history as run cards or the shared
// card-stack empty state, and the actions in the body rather than a dialog footer — because that is the
// screen the same user sees in the app, and a divider list is a desktop shape. `useNarrow` picks; the two
// bodies are deliberately separate trees rather than one tree under media queries, since they differ in
// structure (cards vs rows), not in spacing.
//
// Every route into the chat from here opens the side chat IN PLACE (`useOpenSideChat`) — the modal
// closes and the thread expands over whatever screen you were on. It must NOT navigate: a handoff that
// pushes a route drops the reader on Home, losing the Routines list they were reading. The seeded
// message is the whole mechanism; the assistant is where a routine is reasoned about and changed, so
// none of this becomes an API route.
export function RoutineDetailModal({
  routine,
  spaceName,
  startInEdit = false,
  onClose,
  backAction,
}: {
  routine: WebRoutine;
  spaceName?: string;
  /** Opened straight from the card's "Edit routine" — the same modal, showing its form. */
  startInEdit?: boolean;
  onClose: () => void;
  /** The named back to the drill-in underneath, when this one was pushed onto the side-panel stack. */
  backAction?: { label: string; onBack: () => void };
}) {
  const { t } = useTranslation();
  const openSideChat = useOpenSideChat();
  const { logPageClickBi, logModalActionBi } = useGenericBi('routine-details');
  const { titleOf, scheduleText, nextRunText, activityText, runCountText, pauseText, statusLabel, runsUntilText } = useRoutineCopy();
  const [step, setStep] = useState<'view' | 'edit'>(startInEdit ? 'edit' : 'view');
  const [toggleError, setToggleError] = useState<string | null>(null);
  const narrow = useNarrow();

  const runs = usePulseRuns(routine.id);
  useLoadBi(
    'routine-detail-data',
    { isLoading: runs.isLoading, isError: runs.isError },
    { entity: { id: routine.id, type: ENTITY_TYPE.pulse } },
  );

  const edit = useRoutineEdit(routine, () => setStep('view'));
  const title = titleOf(routine);
  const badge = routineStatusBadge(routine);
  const entity = { entity: { id: routine.id, type: ENTITY_TYPE.pulse } };

  const close = () => {
    logModalActionBi({ ...ROUTINE_DETAILS_PAGE, modal_name: 'routine_detail', action: 'close' });
    onClose();
  };

  const onChat = () => {
    logPageClickBi({ ...ROUTINE_DETAILS_PAGE, button_name: 'chat_about_routine' }, entity);
    onClose();
    openSideChat({ kind: 'new', message: t('routines.chatSeed', { name: title }) });
  };

  const onDelete = async () => {
    logPageClickBi({ ...ROUTINE_DETAILS_PAGE, button_name: 'delete_routine' }, entity);
    onClose();
    await deleteRoutine(routine.id);
  };

  // The same optimistic pause write the card's switch performs (`patchRoutine` owns the rollback), so
  // the two controls over one routine cannot disagree about what a flip does.
  const onToggle = async (next: boolean) => {
    logPageClickBi(
      { ...ROUTINE_DETAILS_PAGE, button_name: 'toggle_routine', state: next ? 'on' : 'off' },
      entity,
    );
    setToggleError(null);
    const result = await patchRoutine(routine, { paused: !next });
    if (!result.ok) setToggleError(t('routines.edit.error.generic'));
  };

  // The Schedule row is a way INTO THE CHAT, not a field and not a picker. A routine's schedule is a
  // cron, and cron says things no picker can ("Weekdays", "the 3rd Thursday") — so rather than offer a
  // control that can only express part of what a routine already holds, pressing the row hands the
  // whole question to the assistant, which can both read the current schedule back and write any shape.
  const onScheduleChat = () => {
    logPageClickBi({ ...ROUTINE_DETAILS_PAGE, button_name: 'change_schedule_in_chat' }, entity);
    onClose();
    openSideChat({ kind: 'new', message: t('routines.schedule.chatSeed', { name: title }) });
  };

  const pause = pauseText(routine);
  // The Activations row is gated on the run WINDOW being known, not on there being a last run —
  // `runCountText` owns that rule, the same one both phones' `routineRunCountLabel` applies.
  const activations = runCountText(routine);
  const hasCron = typeof routine.cron === 'string' && routine.cron.trim().length > 0;

  // The phone's facts card. Every row is gated on there being something true to say, the same rule
  // `RoutineDetailScreen.factRows` applies on both apps — never a placeholder.
  const facts: { label: string; value: string }[] = [];
  if (routine.agentName) facts.push({ label: t('routines.detail.agent'), value: routine.agentName });
  if (spaceName) facts.push({ label: t('routines.detail.space'), value: spaceName });
  const nextRun = nextRunText(routine);
  if (nextRun) facts.push({ label: t('routines.detail.nextRun'), value: nextRun });
  const runsUntil = runsUntilText(routine);
  if (runsUntil) facts.push({ label: t('routines.detail.runsUntil'), value: runsUntil });
  if (activations) facts.push({ label: t('routines.detail.activations'), value: activations });

  return (
    <Modal
      open
      onClose={close}
      size="medium"
      maxHeightPx={650}
      scrollBody
      // The EDIT form is a bottom sheet on a phone, the way both apps present it
      // (`RoutineEditSheet` / `symphonyBottomSheet`). The DETAIL behind it stays full-screen, because
      // that is a screen the phones PUSH — the two are different presentations on native too, and
      // collapsing them here would make a form the user opened from a screen look like the screen.
      presentation={step === 'edit' && narrow ? 'sheet' : 'centered'}
      title={title}
      labelledBy="routine-detail-title"
      backAction={backAction}
      headerSeparator
      footerSeparator={step === 'edit' || !narrow}
      footer={
        step === 'edit' ? (
          <>
            <Button variant="outlined" onClick={() => setStep('view')}>
              {t('common.cancel')}
            </Button>
            {/* A routine with no name is not a routine — Save is blocked rather than writing a blank
                title, exactly as both phones' edit sheets block it. */}
            <Button onClick={() => void edit.save()} disabled={edit.saving || !edit.canSave}>
              {t('common.save')}
            </Button>
          </>
        ) : narrow ? undefined : (
          <>
            {/* The card's row menu keeps the bare "Delete": it sits under "Edit routine" / "Chat about
                this routine", which have already named the subject. Alone in a footer, this one has to. */}
            <TextButton size="small" color="destructive" onClick={() => void onDelete()}>
              {t('routines.action.deleteRoutine')}
            </TextButton>
            <Button variant="outlined" onClick={() => setStep('edit')}>
              {t('routines.action.edit')}
            </Button>
            <Button onClick={onChat}>{t('routines.action.chat')}</Button>
          </>
        )
      }
    >
      <div className={styles.modalBody}>
        {step === 'edit' ? (
          <RoutineEditForm
            fields={edit.fields}
            error={edit.error}
            scheduleText={scheduleText(routine)}
            onEditSchedule={onScheduleChat}
          />
        ) : narrow ? (
          <div className={styles.phoneBody}>
            <Card variant="stone" size="sm" className={styles.phoneCard}>
              <span className={styles.statusLine}>
                <Badge variant={badge.variant} label={statusLabel(routine, badge)} />
                {routine.ended !== true && (
                  <Switch
                    checked={routineIsOn(routine)}
                    onChange={onToggle}
                    disabled={!routineToggleEnabled(routine)}
                    aria-label={t('routines.toggle.label', { name: title })}
                  />
                )}
              </span>
              <SkillConnectorLine slug={routine.sourceConnectorSlug} />
              {pause && <span className={styles.pauseNote}>{pause}</span>}
              {toggleError && <span className={styles.rowError}>{toggleError}</span>}
            </Card>

            {facts.length > 0 && (
              <Card variant="stone" size="sm" className={styles.phoneCard}>
                {facts.map((fact) => (
                  <div key={fact.label} className={styles.factRow}>
                    <span className={styles.factLabel}>{fact.label}</span>
                    <span className={styles.factValue}>{fact.value}</span>
                  </div>
                ))}
              </Card>
            )}

            {routine.description?.trim() && (
              <PhoneSection title={t('routines.detail.whatItDoes')}>
                <Card variant="stone" size="sm" className={styles.phoneCard}>
                  <span className={styles.sectionBody}>{routine.description.trim()}</span>
                </Card>
              </PhoneSection>
            )}

            <PhoneSection title={t('routines.detail.whenItFires')}>
              <Card variant="stone" size="sm" className={styles.phoneCard}>
                <span className={styles.firesKind}>
                  {t(hasCron ? 'routines.detail.onSchedule' : 'routines.detail.onTrigger')}
                </span>
                <span className={styles.firesValue}>{scheduleText(routine)}</span>
              </Card>
            </PhoneSection>

            <PhoneSection title={t('pulses.runs.title')}>
              <PhoneRuns runs={runs.isError ? [] : (runs.data ?? null)} />
            </PhoneSection>

            {/* The dialog footer is gone on a phone — these are the screen's actions, laid out as both
                apps lay them out: chat takes the slack, edit hugs, delete gets its own full-width row. */}
            <div className={styles.phoneActions}>
              <div className={styles.phoneActionsRow}>
                <Button onClick={onChat} className={styles.phoneChat}>
                  {t('routines.action.chat')}
                </Button>
                <Button variant="outlined" onClick={() => setStep('edit')}>
                  {t('routines.action.edit')}
                </Button>
              </div>
              <Button variant="outlined" color="destructive" fullWidth onClick={() => void onDelete()}>
                {t('routines.action.deleteRoutine')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.rows}>
            <DetailRow label={t('routines.detail.status')}>
              <span className={styles.statusLine}>
                {/* A credit-paused routine cannot be resumed from here: writing `paused: false` would be
                    undone by the next credit check, so the control would lie. The pill names the way out. */}
                {routine.ended !== true && (
                  <Switch
                    checked={routineIsOn(routine)}
                    onChange={onToggle}
                    disabled={!routineToggleEnabled(routine)}
                    aria-label={t('routines.toggle.label', { name: title })}
                  />
                )}
                <Badge variant={badge.variant} label={statusLabel(routine, badge)} />
              </span>
              {/* Why it is stopped, in the agent's own words when it left any — otherwise the localized
                  reason. Only ever rendered when the server says there IS a reason. */}
              {pause && <span className={styles.pauseNote}>{pause}</span>}
              {toggleError && <span className={styles.rowError}>{toggleError}</span>}
              {/* Which connected app this routine came with — nothing for a user- or agent-made one. */}
              <SkillConnectorLine slug={routine.sourceConnectorSlug} />
            </DetailRow>
            {routine.agentName && (
              <DetailRow label={t('routines.detail.agent')}>
                <span className={styles.agentTag}>
                  {routine.agentId ? <AgentDot id={routine.agentId} size={15} /> : null}
                  <span className={styles.agentName}>{routine.agentName}</span>
                </span>
              </DetailRow>
            )}
            {spaceName && <DetailRow label={t('routines.detail.space')}>{spaceName}</DetailRow>}
            <DetailRow label={t('routines.detail.schedule')}>{scheduleText(routine)}</DetailRow>
            {nextRunText(routine) && (
              <DetailRow label={t('routines.detail.nextRun')}>{nextRunText(routine)}</DetailRow>
            )}
            {runsUntil && <DetailRow label={t('routines.detail.runsUntil')}>{runsUntil}</DetailRow>}
            {/* Activations are omitted entirely when the run window did not reach this routine —
                a bounded read that saw nothing is not evidence that nothing happened. */}
            {activations && <DetailRow label={t('routines.detail.activations')}>{activations}</DetailRow>}
            {activityText(routine) && (
              <DetailRow label={t('routines.detail.lastRun')}>{activityText(routine)}</DetailRow>
            )}
            {routine.description?.trim() && (
              <DetailRow label={t('routines.detail.whatItDoes')}>
                <span className={styles.sectionBody}>{routine.description.trim()}</span>
              </DetailRow>
            )}
            <DetailRow label={t('routines.detail.recentRuns')}>
              <RecentRuns runs={runs.isError ? [] : (runs.data ?? null)} />
            </DetailRow>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** A phone section: the heading sits ABOVE the card, as it does on both apps. */
function PhoneSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.phoneSection}>
      <h3 className={styles.phoneSectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

/** The run history as the phones draw it: one card per run, or the shared card-stack empty state. */
function PhoneRuns({ runs }: { runs: { conversationId: string; spawnedAt: string; tasks: unknown[] }[] | null }) {
  const { t, i18n } = useTranslation();
  if (runs === null) {
    return (
      <Card variant="stone" size="sm" className={styles.phoneCard}>
        <span className={styles.muted}>{t('common.loading')}</span>
      </Card>
    );
  }
  if (runs.length === 0) return <EmptyState label={t('routines.detail.recentRunsEmpty')} surface="stone" />;
  return (
    <div className={styles.phoneRuns}>
      {runs.map((run) => {
        const count = run.tasks?.length ?? 0;
        return (
          <Card key={run.conversationId} variant="stone" size="sm" className={styles.phoneCard}>
            <span className={styles.phoneRunHead}>
              {`${runDate(run.spawnedAt, i18n?.language)} · ${
                showsTaskCount(count)
                  ? count === 1
                    ? t('pulses.runs.tasksCreatedOne')
                    : t('pulses.runs.tasksCreatedOther', { count })
                  : t('pulses.runs.nothingToReport')
              }`}
            </span>
          </Card>
        );
      })}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <div className={styles.rowValue}>{children}</div>
    </div>
  );
}

function RecentRuns({ runs }: { runs: { conversationId: string; spawnedAt: string; tasks: unknown[] }[] | null }) {
  const { t, i18n } = useTranslation();
  if (runs === null) return <span className={styles.muted}>{t('common.loading')}</span>;
  if (runs.length === 0) return <span className={styles.muted}>{t('pulses.runs.empty')}</span>;
  return (
    <div className={styles.runs}>
      {runs.map((run) => {
        const count = run.tasks?.length ?? 0;
        const what = showsTaskCount(count)
          ? count === 1
            ? t('pulses.runs.tasksCreatedOne')
            : t('pulses.runs.tasksCreatedOther', { count })
          : t('pulses.runs.nothingToReport');
        return (
          <span key={run.conversationId} className={styles.runRow}>
            {`${runDate(run.spawnedAt, i18n?.language)} · ${what}`}
          </span>
        );
      })}
    </div>
  );
}

function runDate(iso: string, locale?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso || '—';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(at);
}

/**
 * The routine drill-in as an entry on the side-panel stack (opened from a task's "From routine" line). It
 * resolves the routine from the routines cache on every render rather than holding a snapshot, so its own
 * switch and edits show at once, and it carries the stack's named back to the task.
 */
export function LiveRoutineDetailModal({ routineId }: { routineId: string }) {
  const { state } = useRoutines();
  const { closeModal, back, backLabel } = useTaskSidePanel();
  const routine = state.kind === 'ready' ? state.routines.find((r) => r.id === routineId) : undefined;
  if (!routine) return null;
  return <RoutineDetailModal routine={routine} onClose={closeModal} backAction={back && backLabel ? { label: backLabel, onBack: back } : undefined} />;
}
